<script setup>
import { computed } from 'vue'
import formatDate from '@utils/format-date-full'
import MixOptions from '@components/mix/mix-options'
import { useQueueService } from '@composables/use-queue-service'

const props = defineProps({
    mix: {
        type: Object,
        default: () => {},
    },
    channel: {
        type: Object,
        default: () => {},
        required: false,
    },
    extended: {
        type: Boolean,
        default: false,
    },
    hasTypeTitle: {
        type: Boolean,
        default: false,
    },
})

const queueService = useQueueService()

const airDate = computed(() => {
    if (props.mix.episode) {
        return formatDate(new Date(props.mix.episode.air_start))
    }
    return ''
})
</script>

<template>
    <div
        v-if="mix"
        class="mix-large-tile"
    >
        <div :class="['container', extended && 'container-extended']">
            <div class="image-wrap">
                <div :class="['cover', { 'cover-classics': channel && channel.id === 'classics' }]">
                    <BaseResourceCover
                        :image-resource="mix.episode"
                        :media-resource="mix"
                        media-type="mix"
                        radius="card"
                    >
                        <template #secondaryActions>
                            <div>
                                <MixOptions :mix="mix" />
                            </div>
                        </template>
                    </BaseResourceCover>
                </div>
            </div>
            <div class="main">
                <BaseLink
                    v-if="mix.artist"
                    :to="{ path: `/artist/${mix.artist.url}/${mix.artist.id}` }"
                >
                    <div class="title">
                        {{ mix.artist.title }}
                    </div>
                </BaseLink>
                <BasePlaceholder
                    v-else
                    class="title"
                    :text="mix.artist && mix.artist.title"
                    type="artist"
                    tag="div"
                />
                <BaseLink :to="{ name: 'mix', params: { id: mix.id } }">
                    <BasePlaceholder
                        class="date"
                        :text="airDate"
                        :size="8"
                        tag="div"
                    />
                </BaseLink>
                <div class="subtitle">
                    <BaseLink
                        v-if="mix.show && mix.show.title"
                        :to="{ name: 'show', params: { id: mix.show.id } }"
                    >
                        {{ mix.show.title }}
                    </BaseLink>
                    <BasePlaceholder
                        v-else
                        :text="mix.show && mix.show.title"
                        type="show"
                    />
                </div>
                <div class="inner">
                    <BaseFavourites
                        v-if="mix"
                        class="inner-item"
                        :mix="mix"
                    />
                </div>
                <span class="play-button">
                    <slot name="button">
                        <BaseButton
                            class="mix-play-button"
                            button-type="secondary"
                            @click="queueService.playMix(mix)"
                        >
                            <span title="PLAY">PLAY</span>
                        </BaseButton>
                    </slot>
                </span>
            </div>
            <div
                v-if="hasTypeTitle"
                class="type-title"
            >Mix</div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.image-wrap {
    position: relative;
    grid-area: image;
    width: span(4 of 12 narrow);
    max-width: 200px;
    margin-bottom: auto;

    &::after {
        display: block;
        width: 100%;
        padding-bottom: 100%;
        content: '';
    }

    @include tablet {
        width: span(2 of 12 narrow);
    }
}

.main {
    display: flex;
    flex-direction: column;
    grid-area: main;
    margin-left: gutter(of 12 narrow);
}

.inner {
    display: grid;
    grid-template-columns: 2fr 2fr 1fr;
    gap: $s-s;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $s-xl;
    .inner-item ~ .inner-item {
        margin-right: 0;
        margin-left: 0;
    }
}

.container {
    display: flex;

    &-extended {
        display: block;
        text-align: center;

        .main {
            width: 100%;
            margin: $s-xl 0 0;
        }
        .image-wrap {
            width: 168px;
            margin: 0 auto;
        }
        .inner {
            margin-right: auto;
            margin-left: auto;
        }

        @include tablet {
            display: grid;
            grid-template-areas: 'image main' 'typeTitle null';
            grid-template-columns: max-content max-content;
            gap: $s-xl;
            align-items: center;
            .main {
                margin-top: unset;
                text-align: left;
            }
            .mix-play-button {
                margin-bottom: 0;
            }
        }
    }

    @include tablet {
        flex-wrap: none;
    }
}

.cover {
    @include fill;

    &-classics {
        filter: grayscale(100%);
    }
}

.title {
    @extend %contentTitleL;

    margin-top: $s-xxs;
    margin-bottom: $s-s;
}

.date {
    @extend %contentTitleM;

    margin-bottom: $s-xxs;
}

.play-button {
    display: none;

    @include desktop {
        display: flex;
        flex-direction: column;
    }
}

.subtitle {
    @extend %contentSubtitleM;

    margin-bottom: $s-m;
}

.type-title {
    @extend %contentTitleM;

    text-align: center;
}
</style>
